import React, { useState } from "react";
import { Avatar, Badge, Box, Paper, Typography, IconButton, Divider } from "@mui/material";
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import ArrowDropUpRoundedIcon from '@mui/icons-material/ArrowDropUpRounded';
import { useSelector } from "react-redux";
import { RootState } from "../../../../store/types";
import { renderBadgeStatus, renderBadgeColor } from "../../../../utils/badgeStatusManipulation";
import { i18n } from "../../../../translate/i18n";

type Props = {
  showEquipmentService: boolean,
}

const EquipmentCard: React.FC<Props> = ({ showEquipmentService }) => {

  const selectedEquipmentWithServices = useSelector((state: RootState) => state.createService.selectedEquipmentWithServices);
  const selectedEquipmentService = useSelector((state: RootState) => state.createService.selectedEquipmentService);
  const [displayAllData, setDisplayAllData] = useState(false);

  const EquipmentData = () => {
    const labels = [
      i18n.t('selectServiceType.department'),
      i18n.t('selectServiceType.brand'),
      i18n.t('selectServiceType.description'),
      'Site: ',
      i18n.t('selectServiceType.reference'),
    ];

    const values = [
      selectedEquipmentWithServices.area?.name || '-',
      selectedEquipmentWithServices.brand || '-',
      selectedEquipmentWithServices.description || '-',
      selectedEquipmentWithServices.site.name,
      selectedEquipmentWithServices.reference || '-',
    ];

    return (
      <Box sx={sxs.equipmentDataSection}>
        <Box sx={sxs.equipmentDataLabels}>
          {labels.map((label, index) => <Typography variant='subtitle1' key={index}>{label}</Typography>)}
        </Box>
        <Box sx={sxs.equipmentDataValues}>
          {values.map((value, index) => <Typography sx={sxs.dataValue} variant='caption' key={index}>{value || ''}</Typography>)}
        </Box>
      </Box>
    )
  }

  return(
    <Box>
      <Box sx={sxs.mainSection}>
        <Box>          
          <Badge
            badgeContent={renderBadgeStatus(selectedEquipmentWithServices)}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            color={renderBadgeColor(selectedEquipmentWithServices)}
            sx={{color: 'white'}}
          >
            {selectedEquipmentWithServices.photo ?
            <Avatar
              src={selectedEquipmentWithServices.photo.url}
              sx={sxs.avatar}
            />
            :
            <Avatar
              sx={sxs.avatarSmallText}
            >
              PHOTO
            </Avatar>
          }
          </Badge>
        </Box>
        <Box>
          <Box sx={sxs.moreDataBox}>
            <Typography variant='subtitle1'>
              {selectedEquipmentWithServices.equipmentModel.name}
            </Typography>
            <IconButton
              children={displayAllData ? <ArrowDropUpRoundedIcon/> : <ArrowDropDownRoundedIcon/>}
              onClick={() => setDisplayAllData(!displayAllData)}
            />
          </Box>
          { showEquipmentService && <Typography variant='subtitle1'>
              {`${i18n.t('selectServiceType.serviceType')} ${selectedEquipmentService.serviceType.name} ${selectedEquipmentService.frequency.name}`}
            </Typography>
          }
          <Typography sx={sxs.equipmentSerialNumber} variant='body2'>
            {`S/N: ${selectedEquipmentWithServices.serialNumber || ''}`}
          </Typography>
          <Typography sx={sxs.equipmentSerialNumber} variant='body2'>
            {`${i18n.t('selectServiceType.place')} ${selectedEquipmentWithServices.place.path}`}
          </Typography>
        </Box>
      </Box>
      {displayAllData && <Box sx={sxs.allData}>
        <EquipmentData />
      </Box>}
    </Box>
  )
}

const sxs = {
  equipmentCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    alignSelf: 'stretch',
  },
  mainSection: {
    display: 'flex',
    flexDirection: 'row',
    gap: '40px'
  },
  avatar: {
    width: '120px',
    height: '120px',
  },
  avatarSmallText: {
    fontSize: '0.75rem',
    width: '120px',
    height: '120px',
    marginLeft: '0.25rem',
  },
  moreDataBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  equipmentSerialNumber: {
    color: '#6F8189',
  },
  equipmentData: {
    display: 'flex',
    gap: 5,
    justifyContent: 'flex-start'
  },
  equipmentDataLabels: {
    display: 'flex',
    flexDirection: 'column'
  },
  equipmentDataValues: {
    display: 'flex',
    flexDirection: 'column'
  },
  dataValue: {
    color: '#6F8189',
    padding: '4px',
  },
  allData: {
    width: '100%',
  },
  equipmentDataSection: {
    display: 'flex',
    marginTop: '48px',
    gap: '48px',
  },
}

export default EquipmentCard;
